import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Briefcase, Baby, RefreshCw, HeartPulse, Sprout, Sun, Wheat, Snowflake, Stethoscope, Video, Network } from "lucide-react";
import { MaskedLines, Reveal, ChapterNumber } from "../components/motion";
import { IMAGES, services } from "../data/content";
import { usePageTitle } from "../lib/title";

const modernCare = [
  {
    icon: Stethoscope,
    title: "Direct Primary Care Memberships",
    body: "A flat monthly fee for a local primary care practice — longer visits, same-day appointments, and your doctor's actual phone number. It pairs with a plan rather than replacing it, and can make everyday care simpler.",
  },
  {
    icon: Video,
    title: "Virtual-First Plans",
    body: "Coverage designed around a virtual doctor as your first stop, with in-person care referred from there. It may suit households that mostly need routine care and like seeing a doctor by phone or video.",
  },
  {
    icon: Network,
    title: "Price-Transparent Providers",
    body: "A growing set of clinics, surgery centers, and care groups that publish their prices and work directly with patients and plans. I can help you find out which ones serve your area and how they fit with your coverage.",
  },
];

const scenarios = [
  {
    icon: Briefcase,
    title: "Self-Employed & 1099",
    body: "Contractors, freelancers, and small-business owners buying their own coverage — marketplace subsidies included, done right.",
  },
  {
    icon: Baby,
    title: "Growing Families",
    body: "New babies, adoptions, and changing households. I size coverage around your pediatricians, prescriptions, and real budget.",
  },
  {
    icon: RefreshCw,
    title: "In Transition",
    body: "Between jobs, retiring early, or leaving a group plan. Short-term bridges and help navigating special enrollment periods.",
  },
  {
    icon: HeartPulse,
    title: "Approaching 65",
    body: "Approaching 65? Medicare help is coming soon. Leave your name and I'll follow up personally when I'm ready to help.",
  },
];

const seasonStrip = [
  [Sprout, "Spring", "Life-change enrollments"],
  [Sun, "Summer", "Mid-year reviews"],
  [Wheat, "Fall", "Open enrollment harvest"],
  [Snowflake, "Winter", "New-year onboarding"],
];

export default function Individuals() {
  usePageTitle("Individual & Family Health Insurance — The Woodlands, TX");
  return (
    <div data-testid="individuals-page">
      {/* HERO */}
      <section className="mx-auto grid max-w-7xl items-center gap-12 px-6 pb-20 pt-36 lg:grid-cols-12 lg:px-10 lg:pt-44" data-testid="individuals-hero">
        <div className="lg:col-span-7">
          <h1 className="font-mono text-xs uppercase tracking-[0.35em] text-terra" data-testid="individuals-kicker">
            Individual &amp; Family Health Insurance · The Woodlands, TX
          </h1>
          <MaskedLines
            as="p"
            className="mt-8 font-serif text-5xl font-light leading-[0.95] tracking-tighter text-forest sm:text-6xl lg:text-7xl"
            lines={[
              "Coverage that fits",
              <span key="l2">
                your family&rsquo;s <em className="text-terra">seasons</em>.
              </span>,
            ]}
          />
          <Reveal delay={0.4}>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-ink/70 sm:text-lg">
              Your health plan should change as your life does. I help Texans choose, understand, and optimize
              individual and family coverage — this year, and every season after it.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/contact?type=consultation"
                data-testid="individuals-hero-cta"
                className="group inline-flex items-center gap-2 rounded-full bg-forest px-8 py-3.5 text-sm font-medium text-cream transition-all duration-300 hover:-translate-y-1 hover:bg-moss"
              >
                Request a Consultation
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to="/education"
                data-testid="individuals-education-cta"
                className="group inline-flex items-center gap-2 rounded-full border border-forest/30 px-8 py-3.5 text-sm font-medium text-forest transition-all duration-300 hover:-translate-y-1 hover:border-gold hover:text-terra"
              >
                Learn the Basics First
              </Link>
            </div>
          </Reveal>
        </div>
        <Reveal className="lg:col-span-4 lg:col-start-9" delay={0.2}>
          <div className="clipped-frame overflow-hidden">
            <img
              src={IMAGES.family}
              alt="Family together in warm light"
              className="h-[380px] w-full object-cover object-top transition-transform duration-700 hover:scale-105 lg:h-[480px]"
              data-testid="individuals-hero-image"
            />
          </div>
        </Reveal>
      </section>

      {/* WHO WE HELP */}
      <section className="bg-sand/70" data-testid="individuals-scenarios">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <Reveal>
            <ChapterNumber n="01" />
            <h2 className="mt-6 font-serif text-4xl font-light leading-[1.05] tracking-tight text-forest sm:text-5xl">
              Wherever you are, <em className="text-terra">I start there</em>.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {scenarios.map((s, i) => (
              <Reveal key={s.title} delay={0.08 * i}>
                <div
                  className="h-full border border-border bg-cream p-8 transition-all duration-500 hover:-translate-y-1 hover:border-gold/60"
                  data-testid={`individuals-scenario-${i}`}
                >
                  <s.icon className="h-6 w-6 text-gold" strokeWidth={1.5} />
                  <h3 className="mt-6 font-serif text-xl font-medium tracking-tight text-forest">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink/65">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE HANDLE */}
      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32" data-testid="individuals-services">
        <Reveal>
          <ChapterNumber n="02" />
          <h2 className="mt-6 font-serif text-4xl font-light leading-[1.05] tracking-tight text-forest sm:text-5xl">
            {services.individuals.title}
          </h2>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink/70">{services.individuals.intro}</p>
        </Reveal>
        <div className="mt-12">
          {services.individuals.items.map((item, i) => (
            <Reveal key={item.title} delay={0.04 * i}>
              <div
                className={`flex gap-4 border-t border-border py-6 ${i === services.individuals.items.length - 1 ? "border-b" : ""}`}
                data-testid={`individuals-service-${i}`}
              >
                <Check className="mt-1 h-4 w-4 shrink-0 text-gold" strokeWidth={2.5} />
                <div>
                  <h3 className="font-serif text-xl font-medium tracking-tight text-forest sm:text-2xl">{item.title}</h3>
                  <p className="mt-1.5 max-w-3xl text-sm leading-relaxed text-ink/65">{item.desc}</p>
                  {item.cta ? (
                    <Link
                      to={item.cta.to}
                      data-testid="medicare-join-list"
                      className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-gold/50 px-4 py-1.5 text-xs font-medium text-terra transition-colors duration-300 hover:border-terra hover:bg-gold/10"
                    >
                      {item.cta.label}
                      <ArrowUpRight className="h-3 w-3" strokeWidth={2} />
                    </Link>
                  ) : null}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* MORE OPTIONS */}
      <section className="bg-sand/70" data-testid="individuals-modern-options">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-terra" data-testid="individuals-modern-kicker">
              More Options Than You&rsquo;ve Been Shown
            </p>
            <h2 className="mt-5 max-w-3xl font-serif text-4xl font-light leading-[1.05] tracking-tight text-forest sm:text-5xl">
              Coverage isn&rsquo;t the only way <em className="text-terra">to get good care</em>
            </h2>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink/70">
              Some of the best answers sit alongside your plan rather than inside it. These are real, available
              today, and almost never mentioned when someone is only selling you a policy.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-3">
            {modernCare.map((item, i) => (
              <Reveal key={item.title} delay={0.06 * i}>
                <div
                  className="h-full border border-border bg-cream p-8 transition-all duration-500 hover:-translate-y-1 hover:border-gold/60"
                  data-testid={`individuals-modern-option-${i}`}
                >
                  <item.icon className="h-6 w-6 text-gold" strokeWidth={1.5} />
                  <h3 className="mt-6 font-serif text-xl font-medium tracking-tight text-forest">{item.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink/65">{item.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SEASON STRIP */}
      <section className="border-y border-border" data-testid="individuals-seasons-strip">
        <div className="mx-auto grid max-w-7xl gap-8 px-6 py-14 sm:grid-cols-2 lg:grid-cols-4 lg:px-10">
          {seasonStrip.map(([Icon, season, note], i) => (
            <Reveal key={season} delay={0.06 * i}>
              <div className="flex items-center gap-4" data-testid={`individuals-season-${season.toLowerCase()}`}>
                <Icon className="h-5 w-5 shrink-0 text-gold" strokeWidth={1.5} />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-terra">{season}</p>
                  <p className="mt-1 font-serif text-lg font-medium text-forest">{note}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32" data-testid="individuals-cta">
        <Reveal>
          <Link
            to="/education/aca-open-enrollment-2027-texas"
            data-testid="individuals-open-enrollment-link"
            className="group mb-10 inline-flex items-center gap-1.5 text-sm font-medium text-terra transition-colors duration-300 hover:text-forest"
          >
            ACA Open Enrollment for 2027 in Texas: pick by Dec 15 for Jan 1
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <h2 className="max-w-3xl font-serif text-4xl font-light leading-[1.05] tracking-tight text-forest sm:text-5xl">
            Your family&rsquo;s coverage deserves <em className="text-terra">a long-term gardener</em>.
          </h2>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-ink/70">
            A consultation costs nothing and commits you to nothing. Bring your doctors, your prescriptions, and
            your questions — leave with a clear picture of your options.
          </p>
          <Link
            to="/contact?type=consultation"
            data-testid="individuals-bottom-cta"
            className="group mt-10 inline-flex items-center gap-2 rounded-full bg-forest px-9 py-4 text-sm font-medium text-cream transition-all duration-300 hover:-translate-y-1 hover:bg-moss"
          >
            Request a Consultation
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
